<template>
    <div class="container-fluid bg-light min-vh-100 d-flex flex-column align-items-center">
      <!-- Header avec navigation et profil -->
      <HeaderPage>
        <div class="card shadow-lg mt-5 p-4 w-100 w-md-75 w-lg-50">
          <div class="text-center">
            <div class="avatar">
              <div class="avatar-circle bg-primary text-white">
                {{ currentUser ? currentUser.nom.charAt(0).toUpperCase() : '?' }}
              </div>
            </div>
            <h2 class="mt-3">
              Bonjour, {{ currentUser ? currentUser.nom : 'Chargement...' }}
            </h2>
            <p class="text-muted">
              <strong>Votre rôle est </strong>
              <span class="badge bg-success">
                {{ currentUser && currentUser.role ? currentUser.role : 'Non défini' }}
              </span>
            </p>
          </div>
  
          <div v-if="rolePermissions.length > 0" class="mt-4">
            <h3 class="text-center text-primary">Vos permissions :</h3>
            <ul class="list-group mt-2">
              <li
                v-for="perm in rolePermissions"
                :key="perm.permission_id"
                class="list-group-item d-flex align-items-center"
              >
                <i class="bi bi-check-circle-fill text-success me-2"></i>
                {{ perm.permission_name }}
              </li>
            </ul>
          </div>
        </div>
  
        <!-- Formulaire de modification du mot de passe -->
        <div class="card shadow-lg mt-5 p-4 w-100 w-md-75 w-lg-50">
          <h3 class="text-center text-secondary mb-4">Modifier votre mot de passe</h3>
          <form @submit.prevent="updatePassword">
            <div class="mb-3">
              <label for="oldPassword" class="form-label">Ancien mot de passe</label>
              <input
                type="password"
                id="oldPassword"
                class="form-control"
                v-model="oldPassword"
                required
              />
            </div>
            <div class="mb-3">
              <label for="newPassword" class="form-label">Nouveau mot de passe</label>
              <input
                type="password"
                id="newPassword"
                class="form-control"
                v-model="newPassword"
                required
              />
            </div>
            <div class="mb-3">
              <label for="confirmPassword" class="form-label">Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                id="confirmPassword"
                class="form-control"
                v-model="confirmPassword"
                required
              />
            </div>
            <button type="submit" class="btn btn-primary w-100">
              Mettre à jour le mot de passe
            </button>
          </form>
          <div
            v-if="passwordMessage"
            class="mt-3 alert"
            :class="{'alert-success': passwordSuccess, 'alert-danger': !passwordSuccess}"
          >
            {{ passwordMessage }}
          </div>
        </div>
      </HeaderPage>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue'
  import axios from 'axios'
  import HeaderPage from './components/HeaderPage.vue'
  
  const currentUser = ref(null)
  const rolePermissions = ref([])
  const API_URL = process.env.VUE_APP_API_URL
  
  // Variables pour le formulaire de modification du mot de passe
  const oldPassword = ref('')
  const newPassword = ref('')
  const confirmPassword = ref('')
  const passwordMessage = ref('')
  const passwordSuccess = ref(false)
  
  // Fonction pour récupérer les données de l'utilisateur connecté via son ID
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token')
      const userId = localStorage.getItem('user_id') // L'ID doit être stocké après la connexion
      if (userId) {
        const response = await axios.get(`${API_URL}/api/user/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        currentUser.value = response.data
      } else {
        console.error("ID de l'utilisateur introuvable dans le localStorage.")
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des données de l'utilisateur :", error)
    }
  }
  
  // Fonction pour récupérer les permissions associées au rôle de l'utilisateur
  const fetchRolePermissions = async () => {
    try {
      if (currentUser.value && currentUser.value.role) {
        const token = localStorage.getItem('token')
        const roleName = currentUser.value.role
        const permResponse = await axios.get(`${API_URL}/api/role/${roleName}/permissionsRole`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        rolePermissions.value = permResponse.data
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des permissions :", error)
    }
  }
  
  // Méthode pour mettre à jour le mot de passe
  async function updatePassword() {
    passwordMessage.value = ''
    if (newPassword.value !== confirmPassword.value) {
      passwordMessage.value = "Les nouveaux mots de passe ne correspondent pas."
      passwordSuccess.value = false
      return
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/user/updatePassword`,
        {
          old_password: oldPassword.value,
          new_password: newPassword.value
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      )
      passwordMessage.value = response.data.message || "Mot de passe mis à jour avec succès."
      passwordSuccess.value = true
      // Réinitialiser les champs
      oldPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''
    } catch (error) {
      console.error("Erreur lors de la mise à jour du mot de passe :", error)
      passwordMessage.value = error.response?.data?.message || "Erreur lors de la mise à jour du mot de passe."
      passwordSuccess.value = false
    }
  }
  
  onMounted(async () => {
    await fetchCurrentUser()
    await fetchRolePermissions()
  })
  </script>
  
  <style scoped>
  /* Styles pour l'avatar */
  .avatar {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .avatar-circle {
    width: 70px;
    height: 70px;
    font-size: 2rem;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
  }
  
  /* Responsive adjustments pour les cartes */
  @media (max-width: 768px) {
    .card {
      width: 90%;
    }
  }
  @media (min-width: 768px) {
    .card {
      width: 75%;
    }
  }
  @media (min-width: 1024px) {
    .card {
      width: 50%;
    }
  }
  </style>
  